<template>
  <LazyHydrate v-bind="hydrationStrategy" @hydrated="onHydration">
    <StandardWrapper
      v-if="parsedCarouselUrls.length"
      v-bind="$attrs"
      :blok="blok"
      width="full"
      no-padding
    >
      <WidthWrapper v-if="blok.title" class="mx-auto my-6">
        <h2
          :id="`imageSliderTitle${blok._uid}`"
          class="title text-center"
          :class="
            textColorClass(blok.titleVariant, blok.bgVariant, 'text-darker')
          "
        >
          {{ blok.title }}
        </h2>
      </WidthWrapper>

      <span :id="`basicDesc${blok._uid}`" class="hidden">
        {{ parsedCarouselUrls.length }} foto's
      </span>

      <silent-box
        v-if="enableLightbox"
        ref="silentbox"
        :gallery="parsedCarouselUrls"
        :lazy-loading="true"
      />

      <div
        :id="`splide-container${blok._uid}`"
        :class="[
          !mounted ? `placeholder-height` : '',
          arrowBgColor ? 'colored-arrows' : '',
          pagination ? 'with-pagination' : '',
        ]"
      >
        <Splide
          v-if="mounted"
          ref="slider"
          :options="splideOptions"
          role="group"
          aria-roledescription="carousel"
          :aria-labelledby="
            blok.title
              ? `imageSliderTitle${blok._uid}`
              : `basicDesc${blok._uid}`
          "
          :aria-live="sliderAutoRotating ? 'off' : 'polite'"
          @splide:click="(splide, e) => onSlideClick(e)"
          @splide:autoplay:play="sliderAutoRotating = true"
          @splide:autoplay:pause="sliderAutoRotating = false"
        >
          <SplideSlide
            v-for="({ imgUrl, alt }, index) in parsedCarouselUrls"
            :key="index"
            :aria-label="`${index + 1} van ${parsedCarouselUrls.length}`"
            role="group"
            aria-roledescription="slide"
            :class="enableLightbox ? 'hover:cursor-pointer' : ''"
          >
            <img
              :src="imgUrl"
              :alt="alt"
              class="h-full w-full object-cover"
              loading="lazy"
            />
          </SplideSlide>
        </Splide>
      </div>
    </StandardWrapper>
  </LazyHydrate>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css'
import StandardWrapper from '../components/StandardWrapper.vue'
import WidthWrapper from '../components/WidthWrapper.vue'
import { usePageContext } from '../renderer/usePageContext.js'
import { useColorPalette } from '../composables/colorPalette'
import { getImgSrc } from '../utils/imgUtils'
import { useHead } from '@unhead/vue'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const { textColorClass, colorCustomClass } = useColorPalette()

const mounted = ref(false)
const sliderAutoRotating = ref(false)
const slider = ref(null)
const silentbox = ref(null)

const hydrationStrategy = computed(() => {
  return {
    whenVisible: { rootMargin: '400px' },
  }
})
const parsedCarouselUrls = computed(() => {
  let companyUrls = []
  const vacancyUrls = []
  let imageUrls = []

  if (
    props.blok.showCompanyImages &&
    props.context.company?.carouselUrls?.length
  ) {
    companyUrls = props.context.company.carouselUrls.map((imgUrl) => {
      return {
        imgUrl: getImgSrc({ imgUrl, height: 550 }),
        src: getImgSrc({ imgUrl }),
        alt: '',
      }
    })
  }
  if (
    props.blok.showVacancyImages &&
    props.context.vacancy?.imageUrls?.length
  ) {
    props.context.vacancy.imageUrls.forEach((imgUrl) => {
      vacancyUrls.push({
        imgUrl: getImgSrc({ imgUrl, height: 550 }),
        fullSizeUrl: getImgSrc({ imgUrl }),
        alt: '',
      })
    })
  }

  if (props.blok.images?.length) {
    imageUrls = props.blok.images.map((image) => {
      return {
        imgUrl: getImgSrc({ imgUrl: image?.filename, height: 550 }),
        src: getImgSrc({ imgUrl: image?.filename }),
        alt: image?.alt || '',
      }
    })
  }

  return [...companyUrls, ...vacancyUrls, ...imageUrls].map(
    (image, index, array) => {
      return { ...image, description: `${index + 1} / ${array.length}` }
    },
  )
})
const splideOptions = computed(() => {
  return {
    autoplay: autoplay.value,
    type: 'loop',
    arrows: arrows.value,
    pagination: pagination.value,
    breakpoints: {
      640: {
        perPage: 1,
        heightRatio: 1 / aspectRatio.value,
      },
      1279: {
        perPage: 2,
        heightRatio: 1 / (aspectRatio.value * 2),
      },
    },
    perPage: slidesPerPage.value,
    heightRatio: 1 / (aspectRatio.value * slidesPerPage.value),
    focus: 'left',
    slideFocus: false,
    keyboard: false,
    paginationKeyboard: false,
    i18n: {
      prev: 'Vorige dia', // Previous slide
      next: 'Volgende dia', // Next slide
      first: 'Ga naar eerste dia', // Go to first slide
      last: 'Ga naar de laatste dia', // Go to last slide
      slideX: 'Ga naar dia %s', // Go to slide %s
      pageX: 'Ga naar pagina %s', // Go to page %s
      play: 'Automatisch afspelen starten', // Start autoplay
      pause: 'Automatisch afspelen pauzeren', // Pause autoplay
      select: 'Selecteer een dia om te tonen', // Select a slide to show
    },
  }
})
const autoplay = computed(() => {
  return props.blok.autoplay || false
})
const arrows = computed(() => {
  return props.blok.arrows || false
})
const pagination = computed(() => {
  return props.blok.pagination || false
})
const slidesPerPage = computed(() => {
  return props.blok.slidesPerPage || 4
})
const aspectRatio = computed(() => {
  return props.blok.aspectRatio || 1.5 // 3:2 aspect ratio by default
})
const enableLightbox = computed(() => {
  return (mounted.value && props.blok.enableLightbox) || false
})
const arrowBgColor = computed(() => {
  if (props.blok.arrowVariant) {
    const color = colorCustomClass(props.blok.arrowVariant, null, '') // Remove the "text" prefix, we only need the color name
    return `var(--${color})`
  }
  return ''
})

function onHydration() {
  mounted.value = true
}
function onSlideClick(slide) {
  if (enableLightbox.value) {
    const index = slide.slideIndex < 0 ? slide.index : slide.slideIndex
    silentbox.value.openOverlay(parsedCarouselUrls.value[index], index)
  }
}
function resetSplider(remount) {
  // In this version of splide some options aren't reactive, so we need to set them again
  slider.value.splide.options = splideOptions.value
  if (remount) {
    slider.value.splide.destroy()
    const splide = slider.value.splide.mount()
    // We need to add again all event listeners after destroying the slider
    splide.on('click', onSlideClick)
  }
}

watch(autoplay, () => {
  resetSplider(true)
})

const pageContext = usePageContext()
useHead(
  () => {
    return {
      style: [
        {
          key: props.blok._uid,
          nonce: pageContext.nonce,
          /* We set a min height in the slider to avoid layout shifts when using SSR */
          innerHTML: `
            #splide-container${props.blok._uid} {
              --slidesPerPage: ${slidesPerPage.value};
              --aspectRatio: ${aspectRatio.value};
              --arrowBgColor: ${arrowBgColor.value};
            }

            #splide-container${props.blok._uid}.placeholder-height {
              --paginationHeight: calc(3.5rem + 18px);
              min-height: calc((100vw / ${slidesPerPage.value}) / ${aspectRatio.value});
            }
            #splide-container${props.blok._uid}.placeholder-height.with-pagination {
              min-height: calc((100vw / ${slidesPerPage.value}) / ${aspectRatio.value} + var(--paginationHeight));
            }
            @media (max-width: 640px) {
              #splide-container${props.blok._uid}.placeholder-height {
                min-height: calc(100vw / ${aspectRatio.value});
              }

              #splide-container${props.blok._uid}.placeholder-height.with-pagination {
                min-height: calc(100vw / ${aspectRatio.value} + var(--paginationHeight));
              }
            }
            @media (min-width: 641px) and (max-width: 1279px) {
              #splide-container${props.blok._uid}.placeholder-height {
                min-height: calc(50vw / ${aspectRatio.value});
              }
              #splide-container${props.blok._uid}.placeholder-height.with-pagination {
                min-height: calc(50vw / ${aspectRatio.value} + var(--paginationHeight));
              }
            }
          `,
        },
      ],
    }
  },
  { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
)
</script>

<style scoped>
@reference "../css/theme.css";
@reference "../css/utilities.css";

/* Don't display the lightbox gallery unless in lightbox mode, since we're using the slider to display images */
:deep(.silentbox-item) {
  display: none;
}
:deep(.splide__pagination) {
  position: relative;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  bottom: 0;
}
:deep(.splide__pagination) .splide__pagination__page {
  @apply focus:outline-default focus:outline-offset-1;
  background-color: #ccc;
  margin-left: 10px;
  margin-right: 10px;
  width: 6px;
  height: 6px;
}
:deep(.splide__pagination) .splide__pagination__page:hover {
  @apply scale-110;
  background-color: var(--default);
}
:deep(.splide__pagination) .splide__pagination__page.is-active {
  @apply scale-125;
  background-color: var(--default);
}

:deep(.splide__arrow) {
  --arrow-bg: white;
  @apply h-16 w-16 rounded-full border-4 border-solid border-white/75 bg-white/0 text-white opacity-75 transition-all duration-200 ease-in-out;
}

.colored-arrows :deep(.splide__arrow) {
  --arrow-bg: var(--arrowBgColor);
  @apply border-white/100 bg-(--arrow-bg)/100 opacity-100;
}

:deep(.splide__arrow) svg {
  @apply h-6 w-6 fill-current;
}

:deep(.splide__arrow:hover) {
  @apply border-white/100 bg-(--arrow-bg)/25 opacity-100;
}

:deep(.splide__arrow:focus) {
  @apply focus-base focus-default;
  outline-offset: 0 !important;
}

.colored-arrows :deep(.splide__arrow:focus) {
  @apply focus-base;
  outline-color: var(--arrow-bg) !important;
}

:deep(.splide__arrow.splide__arrow--prev) {
  @apply md:ml-8;
}

:deep(.splide__arrow.splide__arrow--next) {
  @apply md:mr-8;
}
</style>
